import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, getErrorMessage } from "@/lib/queryClient";
import { formatDateTime } from "@/lib/date-format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle,
  AlertCircle,
  CreditCard,
  Search,
  Filter,
  Clock,
  Monitor,
  MapPin,
} from "lucide-react";
import type { Lesson } from "@contracts/lesson";
import type { Student } from "@contracts/student";

export default function PaymentsPage() {
  const [search, setSearch] = useState("");
  const [studentFilter, setStudentFilter] = useState("all");
  const [paymentTab, setPaymentTab] = useState<"all" | "pending" | "paid">("pending");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const { toast } = useToast();

  const { data: lessons = [], isLoading: lessonsLoading } = useQuery<Lesson[]>({
    queryKey: ["/api/lessons"],
  });

  const { data: students = [] } = useQuery<Student[]>({
    queryKey: ["/api/students"],
  });

  const studentMap = useMemo(() => {
    const map: Record<string, Student> = {};
    students.forEach(s => { map[s.id] = s; });
    return map;
  }, [students]);

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ lessonId, status }: { lessonId: string; status: string }) => {
      const res = await apiRequest("PATCH", `/api/lessons/${lessonId}/status`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      toast({ title: "Payment status updated", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Could not update payment status"), variant: "destructive" });
    },
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async (body: { lessonIds: string[]; status: "paid" | "pending" }) => {
      const res = await apiRequest("PATCH", "/api/lessons/bulk/status", body);
      return res.json() as Promise<{ updated: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      setSelectedIds(new Set());
      toast({
        title: `Updated ${data.updated} lesson${data.updated !== 1 ? "s" : ""}`,
        variant: "success",
      });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Bulk update failed"), variant: "destructive" });
    },
  });

  useEffect(() => {
    setSelectedIds(new Set());
  }, [paymentTab]);

  const filtered = useMemo(() => {
    return lessons.filter(l => {
      const student = studentMap[l.studentId];
      const matchesSearch = student?.name?.toLowerCase().includes(search.toLowerCase()) || false;
      const matchesStudent = studentFilter === "all" || l.studentId === studentFilter;
      return (search === "" || matchesSearch) && matchesStudent;
    });
  }, [lessons, search, studentFilter, studentMap]);

  const pendingLessons = filtered.filter(l => l.paymentStatus === "pending");
  const paidLessons = filtered.filter(l => l.paymentStatus === "paid");

  const pendingByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const l of pendingLessons) {
      const amount = parseFloat(l.unitRate) * l.duration / 60;
      totals[l.unitCurrency] = (totals[l.unitCurrency] || 0) + amount;
    }
    return totals;
  }, [pendingLessons]);

  const paidByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const l of paidLessons) {
      const amount = parseFloat(l.unitRate) * l.duration / 60;
      totals[l.unitCurrency] = (totals[l.unitCurrency] || 0) + amount;
    }
    return totals;
  }, [paidLessons]);

  function toggleRowSelect(id: string) {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  if (lessonsLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid sm:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  function renderLessonRow(lesson: Lesson, showCheckbox: boolean) {
    const student = studentMap[lesson.studentId];
    const amount = parseFloat(lesson.unitRate) * lesson.duration / 60;
    const paymentToggleTooltip =
      lesson.paymentStatus === "paid" ? "Mark as Unpaid" : "Mark as Paid";
    return (
      <Card key={lesson.id} className="p-3 sm:p-4" data-testid={`payment-row-${lesson.id}`}>
        <div className="flex items-center gap-2 sm:gap-3">
          {showCheckbox && (
            <Checkbox
              checked={selectedIds.has(lesson.id)}
              onCheckedChange={() => toggleRowSelect(lesson.id)}
              data-testid={`checkbox-payment-${lesson.id}`}
            />
          )}
          <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${
            lesson.paymentStatus === "paid" ? "bg-green-500" : "bg-amber-500"
          }`} />
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-[10px] sm:text-xs flex-shrink-0">
            {student?.name?.slice(0, 2).toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{student?.name || "Unknown"}</span>
              <Badge variant="outline" className="text-[10px] hidden sm:inline-flex">
                {lesson.lessonType === "online" ? (
                  <><Monitor className="w-2.5 h-2.5 mr-0.5" />online</>
                ) : (
                  <><MapPin className="w-2.5 h-2.5 mr-0.5" />offline</>
                )}
              </Badge>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-muted-foreground mt-0.5">
              <span>{formatDateTime(lesson.scheduledDate)}</span>
              <span className="hidden sm:inline">·</span>
              <span className="hidden sm:inline">{lesson.duration}min</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0 mr-1 sm:mr-3">
            <p className="text-sm font-semibold">{amount.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">{lesson.unitCurrency}</p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={lesson.paymentStatus === "paid" ? "outline" : "default"}
                  size="sm"
                  onClick={() =>
                    toggleStatusMutation.mutate({
                      lessonId: lesson.id,
                      status: lesson.paymentStatus === "paid" ? "pending" : "paid",
                    })
                  }
                  className="hidden sm:inline-flex"
                  data-testid={`button-payment-toggle-${lesson.id}`}
                >
                  {lesson.paymentStatus === "paid" ? (
                    <><CheckCircle className="w-3.5 h-3.5 mr-1.5" />Paid</>
                  ) : (
                    <><Clock className="w-3.5 h-3.5 mr-1.5" />Mark Paid</>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{paymentToggleTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={lesson.paymentStatus === "paid" ? "outline" : "default"}
                  size="icon"
                  className="sm:hidden h-8 w-8 flex-shrink-0"
                  onClick={() =>
                    toggleStatusMutation.mutate({
                      lessonId: lesson.id,
                      status: lesson.paymentStatus === "paid" ? "pending" : "paid",
                    })
                  }
                  data-testid={`button-payment-toggle-mobile-${lesson.id}`}
                >
                  {lesson.paymentStatus === "paid" ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : (
                    <Clock className="w-3.5 h-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{paymentToggleTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </Card>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6 max-w-5xl mx-auto" data-testid="page-payments">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="text-page-title">Payments</h1>
        <p className="text-muted-foreground text-sm mt-1">Track and manage lesson payments</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <CreditCard className="w-4 h-4" />
            <span>Total Lessons</span>
          </div>
          <p className="text-2xl font-bold">{filtered.length}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 mb-1">
            <AlertCircle className="w-4 h-4" />
            <span>Pending</span>
          </div>
          {Object.entries(pendingByCurrency).length === 0 ? (
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">0</p>
          ) : (
            <div className="space-y-0.5">
              {Object.entries(pendingByCurrency).map(([currency, amount]) => (
                <p key={currency} className="text-xl font-bold text-amber-600 dark:text-amber-400">
                  {amount.toFixed(2)} {currency}
                </p>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">{pendingLessons.length} lessons</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span>Paid</span>
          </div>
          {Object.entries(paidByCurrency).length === 0 ? (
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">0</p>
          ) : (
            <div className="space-y-0.5">
              {Object.entries(paidByCurrency).map(([currency, amount]) => (
                <p key={currency} className="text-xl font-bold text-green-600 dark:text-green-400">
                  {amount.toFixed(2)} {currency}
                </p>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">{paidLessons.length} lessons</p>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by student name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-payments"
          />
        </div>
        <Select value={studentFilter} onValueChange={setStudentFilter}>
          <SelectTrigger className="w-48" data-testid="select-student-filter">
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="All Students" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Students</SelectItem>
            {students.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={paymentTab} onValueChange={(v) => setPaymentTab(v as "all" | "pending" | "paid")} data-testid="tabs-payments">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="pending">Pending ({pendingLessons.length})</TabsTrigger>
            <TabsTrigger value="paid">Paid ({paidLessons.length})</TabsTrigger>
            <TabsTrigger value="all">All ({filtered.length})</TabsTrigger>
          </TabsList>
          {paymentTab === "pending" && selectedIds.size > 0 && (
            <Button
              size="sm"
              onClick={() =>
                bulkStatusMutation.mutate({ lessonIds: [...selectedIds], status: "paid" })
              }
              disabled={bulkStatusMutation.isPending}
              data-testid="button-bulk-mark-paid-payments"
            >
              Mark {selectedIds.size} as paid
            </Button>
          )}
          {paymentTab === "paid" && selectedIds.size > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                bulkStatusMutation.mutate({ lessonIds: [...selectedIds], status: "pending" })
              }
              disabled={bulkStatusMutation.isPending}
              data-testid="button-bulk-mark-unpaid-payments"
            >
              Mark {selectedIds.size} as unpaid
            </Button>
          )}
        </div>

        <TabsContent value="all" className="mt-4">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="font-medium mb-1">No payments found</h3>
              <p className="text-sm text-muted-foreground">Lessons will appear here once you create them</p>
            </div>
          ) : (
            <div className="space-y-2">{filtered.map((l) => renderLessonRow(l, false))}</div>
          )}
        </TabsContent>
        <TabsContent value="pending" className="mt-4">
          {pendingLessons.length === 0 ? (
            <div className="py-16 text-center">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500/30" />
              <h3 className="font-medium mb-1">All paid up!</h3>
              <p className="text-sm text-muted-foreground">No pending payments</p>
            </div>
          ) : (
            <div className="space-y-2">{pendingLessons.map((l) => renderLessonRow(l, true))}</div>
          )}
        </TabsContent>
        <TabsContent value="paid" className="mt-4">
          {paidLessons.length === 0 ? (
            <div className="py-16 text-center">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="font-medium mb-1">No paid lessons yet</h3>
              <p className="text-sm text-muted-foreground">Mark lessons as paid to see them here</p>
            </div>
          ) : (
            <div className="space-y-2">{paidLessons.map((l) => renderLessonRow(l, true))}</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
